import { useCallback } from 'react';
import logger from '../../../utils/logger';
import { getCommentRecordId, getReplyRecordId } from '../../../utils/commentIds';

export function useCommentLikeActions({
  likeComment,
  unlikeComment,
  likeReply,
  unlikeReply,
}) {
  const handleLike = useCallback(
    (songId, comment, userId, reply = null, commentIndex = null, replyIndex = null) => {
      if (!userId) {
        return Promise.reject(new Error('User must be signed in to like'));
      }

      const commentId = getCommentRecordId(comment);
      if (!commentId && commentIndex == null) {
        const error = new Error('Unable to like comment: missing identifiers');
        logger.warn(error.message, { commentId, commentIndex, userId });
        return Promise.reject(error);
      }

      if (reply) {
        const replyId = getReplyRecordId(reply);
        if (!replyId && replyIndex == null) {
          const error = new Error('Unable to like reply: missing identifiers');
          logger.warn(error.message, { commentId, commentIndex, replyIndex });
          return Promise.reject(error);
        }
        return likeReply(
          songId,
          commentId,
          replyId,
          userId,
          commentIndex,
          replyIndex
        );
      }

      return likeComment(songId, commentId, userId, commentIndex);
    },
    [likeReply, likeComment]
  );

  const handleUnlike = useCallback(
    (songId, comment, userId, reply = null, commentIndex = null, replyIndex = null) => {
      if (!userId) {
        return Promise.reject(new Error('User must be signed in to unlike'));
      }

      const commentId = getCommentRecordId(comment);
      if (!commentId && commentIndex == null) {
        const error = new Error('Unable to unlike comment: missing identifiers');
        logger.warn(error.message, { commentId, commentIndex, userId });
        return Promise.reject(error);
      }

      if (reply) {
        const replyId = getReplyRecordId(reply);
        if (!replyId && replyIndex == null) {
          const error = new Error('Unable to unlike reply: missing identifiers');
          logger.warn(error.message, { commentId, commentIndex, replyIndex });
          return Promise.reject(error);
        }
        return unlikeReply(
          songId,
          commentId,
          replyId,
          userId,
          commentIndex,
          replyIndex
        );
      }

      return unlikeComment(songId, commentId, userId, commentIndex);
    },
    [unlikeReply, unlikeComment]
  );

  return { handleLike, handleUnlike };
}
